import React from "react";

import { cn } from "@/lib/utils";
import type { WeekBar } from "@/lib/week-bars";

/** Высота области столбцов, px. */
const BARS_HEIGHT = 64;
/** Ширина одной колонки в координатах SVG; SVG тянется по ширине плитки
 *  (`preserveAspectRatio="none"` — тянутся только прямоугольники, текст — в HTML). */
const COL = 10;
const BAR_W = 7;

/**
 * Мини-столбцы по неделям: значения над выделенным и текущим столбцом, столбцы
 * (inline SVG), подписи недель под ними. Модель — `lib/week-bars.ts::weekBars`.
 * Текст — в HTML-сетке с тем же числом колонок: в растянутом SVG он бы плыл.
 * Подсказка — `title` на колонку целиком (цель наведения больше столбца).
 */
export function WeekBars({ bars }: { bars: WeekBar[] }): React.ReactElement {
  const n = bars.length;
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` };
  const width = n * COL;
  const hatch = `week-bars-hatch-${React.useId().replace(/:/g, "")}`;
  return (
    <div className="select-none" data-testid="week-bars">
      <div className="grid text-center text-[11px] leading-4 tabular-nums" style={grid}>
        {bars.map((b) => (
          <span
            key={b.key}
            className={cn(
              "truncate",
              b.tone === "last" ? "font-medium text-slate-800" : "text-muted-foreground",
            )}
          >
            {b.valueLabel ?? ""}
          </span>
        ))}
      </div>
      <svg
        width="100%"
        height={BARS_HEIGHT}
        viewBox={`0 0 ${width} ${BARS_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={bars.map((b) => b.title).join("; ")}
        className="block"
      >
        <defs>
          <pattern
            id={hatch}
            width="3"
            height="3"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="3" height="3" className="fill-slate-100" />
            <line x1="0" y1="0" x2="0" y2="3" className="stroke-slate-400" strokeWidth="1.2" />
          </pattern>
        </defs>
        <line
          x1={0}
          x2={width}
          y1={BARS_HEIGHT - 0.5}
          y2={BARS_HEIGHT - 0.5}
          className="stroke-slate-200"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        {bars.map((b, i) => {
          // Ненулевое — хотя бы 2 px, чтобы малое значение не читалось нулём.
          const h = b.ratio > 0 ? Math.max(2, b.ratio * (BARS_HEIGHT - 2)) : 0;
          const x = i * COL + (COL - BAR_W) / 2;
          return (
            <g key={b.key} data-tone={b.tone}>
              <title>{b.title}</title>
              <rect x={i * COL} y={0} width={COL} height={BARS_HEIGHT} fill="transparent" />
              {h > 0 && (
                <rect
                  x={x}
                  y={BARS_HEIGHT - h}
                  width={BAR_W}
                  height={h}
                  className={cn(
                    b.tone === "last" && "fill-primary",
                    b.tone === "past" && "fill-slate-300",
                    b.tone === "current" && "stroke-slate-400",
                  )}
                  fill={b.tone === "current" ? `url(#${hatch})` : undefined}
                  strokeWidth={b.tone === "current" ? 1 : undefined}
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </g>
          );
        })}
      </svg>
      <div className="mt-0.5 grid text-center text-[10px] leading-3 tabular-nums" style={grid}>
        {bars.map((b) => (
          <span
            key={b.key}
            className={cn(
              "truncate",
              b.tone === "last" ? "font-medium text-slate-700" : "text-muted-foreground",
              b.tone === "current" && "italic",
            )}
          >
            {b.showLabel ? b.label : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
